/**
 * MeetingHeaderView — Phase 6 free-flow editor with the Phase 4.5
 * meta-strip styling restored (per pilot feedback the pill layout
 * felt too playful for a formal meeting-minutes doc).
 *
 * Layout matches the legacy preview:
 *   - Variant eyebrow ("CLIENT MEETING" / "INTERNAL")
 *   - h1 title — editable via NodeViewContent
 *   - 3-column meta grid (Meeting date / Notes date / Format)
 *   - Project (full-width, with /projects?q= typeahead)
 *
 * Wave-3 audit fixes:
 *   - Native <input type="date"> replaced with a custom popover
 *     trigger that displays the formatted date and opens an
 *     input inside a styled popover. Same affordance shape as
 *     ActionItemView's DueDatePillButton, so the editor feels
 *     consistent. (#5)
 *   - Native <select> for meetingFormat replaced with a custom
 *     popover that shows the four options as clickable rows in
 *     the OJB pine palette. (#14)
 *   - Project field now offers /projects?q= typeahead with the
 *     same dropdown semantics as ProjectPicker on the new-meeting
 *     route — no more typo-driven duplicates from a bare text
 *     input. (#4)
 *
 * All three popovers route through the shared usePopover hook so
 * outside-click + Escape + focus restoration behave identically.
 */
import { useEffect, useRef, useState } from 'react';
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { formatHumanDate } from '../dateFormat.ts';
import { usePopover } from '../usePopover.ts';
import { api } from '../../../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../../../lib/noAutofill.ts';

interface HeaderAttrs {
  project?: string;
  meetingDate?: string;
  notesDate?: string;
  location?: string;
  meetingFormat?: 'in_person' | 'video' | 'phone' | 'hybrid';
  templateVariant?: 'client_meeting' | 'internal';
}

const FORMAT_OPTIONS: Array<{
  value: NonNullable<HeaderAttrs['meetingFormat']>;
  label: string;
}> = [
  { value: 'in_person', label: 'In person' },
  { value: 'video', label: 'Video' },
  { value: 'phone', label: 'Phone' },
  { value: 'hybrid', label: 'Hybrid' },
];

function formatLabel(f: HeaderAttrs['meetingFormat']): string {
  return FORMAT_OPTIONS.find((opt) => opt.value === f)?.label ?? '-';
}

export function MeetingHeaderView({
  node,
  updateAttributes,
  editor,
}: NodeViewProps) {
  const attrs = node.attrs as HeaderAttrs;
  const isEditable = editor.isEditable;

  // Stop input events from bubbling into ProseMirror so cursor /
  // selection / focus stay on the input the user is interacting
  // with.
  const stopPm = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <NodeViewWrapper className="tt-meeting-header" data-node="meetingHeader">
      {/* Variant kicker ("CLIENT MEETING" / "INTERNAL") intentionally
          NOT rendered for the pilot — single unified format. The
          templateVariant attr stays on the node for round-trip
          compatibility but it's invisible everywhere. */}

      {/* Editable title — NodeViewContent renders the headerTitle
          child here. The title participates in the editor's
          contenteditable surface, so B/I/U marks, selection,
          BubbleMenu, etc. all work as expected. */}
      <NodeViewContent
        as="div"
        className="tt-meeting-header__title-slot mt-2"
      />

      {/* Location subtitle — directly under the title, italic
          pine. The previous version rendered the input with no
          border/background which made it look like static text;
          users couldn't tell it was clickable. Now hover gives
          a subtle pine tint + text cursor, focus shows a dotted
          underline so the affordance is clear. */}
      {(isEditable || attrs.location) && (
        <div
          contentEditable={false}
          className="mt-1 text-[11px] italic text-pine"
        >
          {isEditable ? (
            <input
              type="text"
              value={attrs.location ?? ''}
              onChange={(e) => updateAttributes({ location: e.target.value })}
              onMouseDown={stopPm}
              onKeyDown={stopPm}
              placeholder="Add a location…"
              className="w-full cursor-text rounded-sm border-0 border-b border-dashed border-transparent bg-transparent p-0 italic text-pine placeholder:text-pine placeholder:italic hover:border-pine/40 hover:bg-pine/5 focus:border-pine focus:bg-pine/10 focus:outline-none focus:ring-0"
            />
          ) : (
            <span>{attrs.location}</span>
          )}
        </div>
      )}

      {/* Meta strip — Row 1: Meeting Date / Notes Date / Format
          (3-up, compact). Row 2: Project (full width). Location
          moved to a subtitle under the title (above). */}
      <div contentEditable={false} className="mt-3 flex flex-col gap-y-1">
        <dl className="grid grid-cols-3 gap-x-3 text-[12px]">
          <MetaCell label="Meeting date">
            <DatePopover
              value={attrs.meetingDate ?? ''}
              isEditable={isEditable}
              onChange={(next) =>
                updateAttributes({ meetingDate: next || null })
              }
            />
          </MetaCell>

          <MetaCell label="Notes date">
            <DatePopover
              value={attrs.notesDate ?? ''}
              isEditable={isEditable}
              onChange={(next) =>
                updateAttributes({ notesDate: next || null })
              }
            />
          </MetaCell>

          <MetaCell label="Format">
            <FormatPopover
              value={attrs.meetingFormat ?? 'in_person'}
              isEditable={isEditable}
              onChange={(next) => updateAttributes({ meetingFormat: next })}
            />
          </MetaCell>
        </dl>

        <dl className="grid grid-cols-1 gap-x-3 text-[12px]">
          <MetaCell label="Project">
            <ProjectAutocomplete
              value={attrs.project ?? ''}
              isEditable={isEditable}
              onChange={(next) => updateAttributes({ project: next })}
            />
          </MetaCell>
        </dl>
      </div>
    </NodeViewWrapper>
  );
}

/** Tiny sub-component for an editable meta-strip cell. Keeps the
 *  label + value alignment consistent across cells. */
function MetaCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-pine">
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

/* ─── Date popover ───────────────────────────────────────────────────
 *
 * Replaces the bare native <input type="date">. The TRIGGER is a
 * styled value display ("Mar 15, 2026" / "—"); clicking opens a
 * popover that hosts the native input. We keep the native input
 * inside the popover so the OS-level calendar UI still works and
 * we don't have to ship a hand-rolled calendar grid — but the
 * trigger is consistent with every other pill in the editor.
 */

function DatePopover({
  value,
  isEditable,
  onChange,
}: {
  value: string;
  isEditable: boolean;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const valueRef = useRef(value);
  valueRef.current = value;

  const popover = usePopover<HTMLSpanElement>({
    onCommit: () => {
      if (draftRef.current !== valueRef.current) onChange(draftRef.current);
    },
  });

  const display = value ? formatHumanDate(value) : '';

  if (!isEditable) {
    return (
      <span className="text-charcoal">{display || '-'}</span>
    );
  }

  return (
    <span ref={popover.wrapperRef} className="relative inline-block">
      <button
        type="button"
        onClick={popover.toggle}
        className={
          'cursor-pointer rounded-sm px-1 py-0.5 text-charcoal transition-colors hover:bg-pine/10 hover:text-pine ' +
          (popover.isOpen ? 'bg-pine/10 text-pine ' : '') +
          (display ? '' : 'italic text-charcoal/60')
        }
      >
        {display || 'Add date'}
      </button>
      {popover.isOpen && (
        <span className="absolute left-0 top-full z-30 mt-1 block rounded-lg border border-charcoal/20 bg-white p-2 shadow-xl">
          <input
            type="date"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                popover.close();
              }
            }}
            className="rounded border border-charcoal/25 px-2 py-1 text-[12px] focus:border-pine focus:outline-none"
          />
          <div className="mt-1.5 flex justify-between gap-1 text-[11px]">
            {value ? (
              <button
                type="button"
                onClick={() => {
                  setDraft('');
                  onChange('');
                  popover.close();
                }}
                className="text-charcoal hover:text-pine"
              >
                Clear
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={() => popover.close()}
              className="rounded-full bg-pine px-2.5 py-0.5 text-cloud hover:bg-pine/90"
            >
              Save
            </button>
          </div>
        </span>
      )}
    </span>
  );
}

/* ─── Format popover ────────────────────────────────────────────────
 *
 * Replaces the native <select>. Four options render as clickable
 * rows; the current value gets a pine highlight. Same trigger
 * shape as DatePopover so the meta strip reads consistently.
 */

function FormatPopover({
  value,
  isEditable,
  onChange,
}: {
  value: NonNullable<HeaderAttrs['meetingFormat']>;
  isEditable: boolean;
  onChange: (next: NonNullable<HeaderAttrs['meetingFormat']>) => void;
}) {
  const popover = usePopover<HTMLSpanElement>();

  if (!isEditable) {
    return <span className="text-charcoal">{formatLabel(value)}</span>;
  }

  return (
    <span ref={popover.wrapperRef} className="relative inline-block">
      <button
        type="button"
        onClick={popover.toggle}
        className={
          'inline-flex cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5 text-charcoal transition-colors hover:bg-pine/10 hover:text-pine ' +
          (popover.isOpen ? 'bg-pine/10 text-pine' : '')
        }
      >
        {formatLabel(value)}
        <ChevronDown size={11} strokeWidth={2.5} aria-hidden />
      </button>
      {popover.isOpen && (
        <span className="absolute left-0 top-full z-30 mt-1 flex flex-col rounded-lg border border-charcoal/20 bg-white p-1 shadow-xl">
          {FORMAT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                popover.close();
              }}
              className={
                'rounded px-2 py-1 text-left text-[12px] transition-colors ' +
                (opt.value === value
                  ? 'bg-pine/10 text-pine'
                  : 'text-charcoal hover:bg-pine/10 hover:text-pine')
              }
            >
              {opt.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

/* ─── Project autocomplete ──────────────────────────────────────────
 *
 * /projects?q= typeahead. Shows up to 8 suggestions; clicking
 * commits the project name. Free-typed values (Enter or blur)
 * are accepted as-is so the user can create a new project on
 * the fly — but they see existing matches first so typos like
 * "riverside park" vs "Riverside Park" are surfaced before they
 * become duplicate records.
 */

interface ProjectSuggestion {
  id: string;
  name: string;
  usageCount: number;
}

function ProjectAutocomplete({
  value,
  isEditable,
  onChange,
}: {
  value: string;
  isEditable: boolean;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  // Keep draft in sync with the prop on external changes (autosave
  // round-trip, AI rewrite landing).
  useEffect(() => setDraft(value), [value]);

  // Close suggestion dropdown on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        // Persist whatever the user typed (free-typed names are
        // valid — server creates-or-reuses on save).
        if (draft !== value) onChange(draft);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [isOpen, draft, value]);

  const suggestions = useQuery({
    queryKey: ['projects-autocomplete', draft],
    queryFn: () =>
      api.get<{ projects: ProjectSuggestion[] }>('/projects', {
        q: draft,
        limit: 8,
      }),
    enabled: isOpen && isEditable,
    staleTime: 60_000,
  });

  // Filter exact-match away from suggestions — if user has already
  // typed the full project name, we don't need to suggest it again.
  const items = (suggestions.data?.projects ?? []).filter(
    (p) => p.name.toLowerCase() !== draft.trim().toLowerCase(),
  );

  if (!isEditable) {
    return <span className="text-charcoal">{value || '-'}</span>;
  }

  const commit = (name: string) => {
    setDraft(name);
    onChange(name);
    setIsOpen(false);
  };

  return (
    <span ref={wrapperRef} className="relative block">
      <input
        {...NO_AUTOFILL}
        type="text"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setIsOpen(true);
          setHighlightIndex(0);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && items.length > 0) {
            e.preventDefault();
            setHighlightIndex((i) => Math.min(i + 1, items.length - 1));
          } else if (e.key === 'ArrowUp' && items.length > 0) {
            e.preventDefault();
            setHighlightIndex((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (items.length > 0 && isOpen) {
              commit(items[highlightIndex].name);
            } else {
              if (draft !== value) onChange(draft);
              setIsOpen(false);
            }
          } else if (e.key === 'Escape') {
            setIsOpen(false);
          }
        }}
        placeholder="-"
        className="w-full border-0 bg-transparent p-0 text-charcoal placeholder:text-charcoal placeholder:italic focus:outline-none focus:ring-0"
      />
      {isOpen && items.length > 0 && (
        <ul className="absolute left-0 top-full z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-smoke/30 bg-white text-[11px] shadow-md">
          {items.map((p, i) => (
            <li key={p.id}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(p.name);
                }}
                onMouseEnter={() => setHighlightIndex(i)}
                className={
                  'flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left transition-colors ' +
                  (i === highlightIndex
                    ? 'bg-pine/10 text-pine'
                    : 'text-charcoal hover:bg-cloud/50')
                }
              >
                <span className="truncate">{p.name}</span>
                {p.usageCount > 0 && (
                  <span className="shrink-0 text-[10px] text-charcoal/50">
                    {p.usageCount}×
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
